import React from "react";
import Loader from "src/components/Loader";
import { parseCommentContent } from "../helpers";
import { EditCommentDialogState } from "../types";

interface EditCommentsDialogProps {
  editCommentDialog: EditCommentDialogState;
  setEditCommentDialog: React.Dispatch<
    React.SetStateAction<EditCommentDialogState>
  >;
  handleUpdateComment: (commentId: string, newComment: string) => void;
}

export const EditCommentsDialog: React.FC<EditCommentsDialogProps> = ({
  editCommentDialog,
  setEditCommentDialog,
  handleUpdateComment,
}) => {
  if (!editCommentDialog.isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Comments</h2>
          <button
            onClick={() =>
              setEditCommentDialog((prev) => ({ ...prev, isOpen: false }))
            }
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {editCommentDialog.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : editCommentDialog.comments.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No comments found</p>
        ) : (
          <div className="space-y-4">
            {editCommentDialog.comments.map((comment) => (
              <div key={comment.id} className="border rounded-lg p-4">
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Original Query
                  </label>
                  <div className="text-sm text-gray-900">
                    {parseCommentContent(comment.content).query}
                  </div>
                </div>

                {editCommentDialog.selectedComment?.id === comment.id ? (
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Edit Comment
                    </label>
                    <textarea
                      className="w-full p-2 border rounded-lg"
                      value={
                        editCommentDialog.selectedComment?.content
                          ? parseCommentContent(
                              editCommentDialog.selectedComment.content,
                            ).comment
                          : ""
                      }
                      onChange={(e) =>
                        setEditCommentDialog((prev) => {
                          if (!prev.selectedComment) return prev;
                          return {
                            ...prev,
                            selectedComment: {
                              ...prev.selectedComment,
                              content: prev.selectedComment.content.replace(
                                /Admin suggestion: .*?\n/,
                                `Admin suggestion: ${e.target.value}\n`,
                              ),
                            },
                          };
                        })
                      }
                      rows={3}
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        className="flex-1 bg-primary text-white py-2 px-4 rounded hover:bg-primary/80"
                        onClick={() =>
                          handleUpdateComment(
                            comment.id,
                            parseCommentContent(
                              editCommentDialog.selectedComment?.content || "",
                            ).comment,
                          )
                        }
                      >
                        Save
                      </button>
                      <button
                        className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
                        onClick={() =>
                          setEditCommentDialog((prev) => ({
                            ...prev,
                            selectedComment: undefined,
                          }))
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Comment
                      </label>
                      <div className="text-sm text-gray-900">
                        {parseCommentContent(comment.content).comment}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="text-primary hover:text-primary/80"
                        onClick={() =>
                          setEditCommentDialog((prev) => ({
                            ...prev,
                            selectedComment: comment,
                          }))
                        }
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-600 hover:text-red-700"
                        onClick={() =>
                          setEditCommentDialog((prev) => ({
                            ...prev,
                            showDeleteConfirm: true,
                            selectedComment: comment,
                          }))
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
